// Shared Gantt-dependency helpers.
//
// Three responsibilities:
//   1. `classifyLink` — given a Jira link entry on an issue and one
//      configured rule, decide whether the link is that dependency
//      type AND who is the predecessor / successor. Used both for
//      drawing existing-link arrows and for building the solver graph.
//   2. `buildDependencyEdges` — walk every visible item's `issuelinks`
//      and emit the full list of dependency edges, deduped.
//   3. `applyConstraintsLocally` — a mirror of the server-side solver
//      so the UI can paint optimistic updates while the propagate-dates
//      call is in flight. Same algorithm, same cycle handling.
//
// All four dependency types use the same rule shape:
//   { linkTypeId, linkTypeName, predecessorSide: 'inward' | 'outward', lagDays }
// Rules may be null/missing when the admin hasn't configured that type.

export const DEPENDENCY_TYPES = ['FS', 'FF', 'SS', 'SF'];

// Pick the type from (sourceDotSide, targetDotSide) per the drag rule:
//   finish → start  = FS
//   finish → finish = FF
//   start  → start  = SS
//   start  → finish = SF
export function deriveType(sourceSide, targetSide) {
  if (sourceSide === 'finish' && targetSide === 'start') return 'FS';
  if (sourceSide === 'finish' && targetSide === 'finish') return 'FF';
  if (sourceSide === 'start' && targetSide === 'start') return 'SS';
  if (sourceSide === 'start' && targetSide === 'finish') return 'SF';
  return null;
}

// Build the Jira POST payload for creating a link.
//
// `predecessorSide` names the Jira link *verb* whose direction
// describes the dependency:
//   'outward' → the outward verb describes pred → succ
//              (e.g. "blocks": pred blocks succ)
//   'inward'  → the inward verb describes pred → succ
//              (e.g. "is blocked by": pred is blocked by succ)
//
// Jira's POST `outwardIssue`/`inwardIssue` fields are the destination /
// source of the outward verb (verb flows inwardIssue → outwardIssue).
// So when the outward verb describes pred → succ, predecessor goes in
// `inwardIssue` and successor in `outwardIssue`.
export function buildLinkPayload(rule, predKey, succKey) {
  if (!rule) return null;
  const predOnInward = rule.predecessorSide === 'outward';
  return {
    type: { name: rule.linkTypeName },
    outwardIssue: { key: predOnInward ? succKey : predKey },
    inwardIssue:  { key: predOnInward ? predKey : succKey },
  };
}

// Given one of the focal issue's `issuelinks` entries and one rule,
// return `{ predKey, succKey, lagDays }` if it matches, else null.
//
// Jira issuelinks are encoded relative to the focal issue:
//   - if `outwardIssue` is present, the OTHER issue is on the outward
//     side of the link → focal issue is on the inward side.
//   - if `inwardIssue` is present, focal is on the outward side.
//
// `rule.predecessorSide` is the verb that represents the dependency
// (see buildLinkPayload). The subject of that verb (the predecessor)
// sits on the OPPOSITE side of the link to the side the verb names:
//   verb 'outward' (verb flows inward → outward) → predecessor on inward side
//   verb 'inward'  (verb flows outward → inward) → predecessor on outward side
export function classifyLink(focalKey, link, rule) {
  if (!rule || !link || !rule.linkTypeId) return null;
  // Match by id OR (case-insensitive) name: link rows and rules saved
  // before the link-type id normalization can carry the type NAME as id
  // ('Link' vs the numeric Jira id), so an id-only compare silently
  // drops the dependency edge.
  const linkTypeId = link.type?.id ? String(link.type.id) : null;
  const idMatch = linkTypeId != null && linkTypeId === String(rule.linkTypeId);
  const nameMatch = !!rule.linkTypeName && !!link.type?.name &&
    String(link.type.name).toLowerCase() === String(rule.linkTypeName).toLowerCase();
  if (!idMatch && !nameMatch) return null;

  const otherKey = link.outwardIssue?.key ?? link.inwardIssue?.key;
  if (!otherKey) return null;
  const focalOnInwardSide = !!link.outwardIssue;
  const predOnInwardSide = (rule.predecessorSide === 'outward');

  if (focalOnInwardSide === predOnInwardSide) {
    return { predKey: focalKey, succKey: otherKey, lagDays: rule.lagDays || 0 };
  }
  return { predKey: otherKey, succKey: focalKey, lagDays: rule.lagDays || 0 };
}

// Walk every visible item's links and build the full edges list.
// Returns `[{ predKey, succKey, type, lagDays, linkId }, ...]`.
//
// Deduplicated by linkId — Jira returns the same link from both ends
// of the relationship, but each end exposes the same internal link id.
export function buildDependencyEdges(items, rules) {
  const edges = [];
  const seenLinkIds = new Set();
  for (const item of items || []) {
    const links = item?.fields?.issuelinks;
    if (!Array.isArray(links)) continue;
    for (const link of links) {
      if (!link?.id || seenLinkIds.has(link.id)) continue;
      for (const type of DEPENDENCY_TYPES) {
        const rule = rules?.[type];
        const hit = classifyLink(item.key, link, rule);
        if (hit) {
          edges.push({
            type,
            predKey: hit.predKey,
            succKey: hit.succKey,
            lagDays: hit.lagDays,
            linkId: link.id,
          });
          seenLinkIds.add(link.id);
          break; // one type wins per link
        }
      }
    }
  }
  return edges;
}

// Reject a new link if any existing edge already connects the same
// ordered pair (either direction). Decision H: one dependency per
// ordered pair.
export function pairAlreadyLinked(edges, keyA, keyB) {
  for (const e of edges) {
    if ((e.predKey === keyA && e.succKey === keyB) ||
        (e.predKey === keyB && e.succKey === keyA)) {
      return true;
    }
  }
  return false;
}

// ──────────────────────────────────────────────────────────────────
// Constraint solver (frontend mirror of the backend one). Identical
// algorithm; the backend version also persists, this one is for
// optimistic UI between drag-end and the network round-trip.
// ──────────────────────────────────────────────────────────────────

// Days between two YYYY-MM-DD strings. Returns null if either is empty.
function dateDiffDays(a, b) {
  if (!a || !b) return null;
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Math.round(ms / 86400000);
}

export function addDaysISO(iso, days) {
  if (!iso) return null;
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// Working-day helpers (skip Sat/Sun + configured vacations). Mirrors
// the backend solver's helpers so the optimistic UI cascades use the
// same math the server will eventually apply.
export function isWeekend(iso) {
  const d = new Date(iso + 'T00:00:00Z');
  const day = d.getUTCDay();
  return day === 0 || day === 6;
}
export function isInVacation(iso, vacations) {
  if (!vacations || !vacations.length) return false;
  for (const v of vacations) {
    if (iso >= v.startDate && iso <= v.endDate) return true;
  }
  return false;
}
export function isWorkingDay(iso, vacations) {
  return !isWeekend(iso) && !isInVacation(iso, vacations);
}
export function addWorkingDays(iso, n, vacations) {
  if (!iso || n == null) return iso;
  if (n === 0) return iso;
  let d = iso;
  const step = n > 0 ? 1 : -1;
  let remaining = Math.abs(n);
  while (remaining > 0) {
    d = addDaysISO(d, step);
    if (isWorkingDay(d, vacations)) remaining--;
  }
  return d;
}

// Snap an ISO date to the nearest working day. If iso is already a
// working day, returns it unchanged. Otherwise walks outward in both
// directions and returns whichever working day is fewer calendar days
// away (forward wins on a tie, matching how the eye scans left→right).
// Used by the Gantt drag handler to keep bar edges always on working
// days during the drag itself (Jira-style UX).
export function nearestWorkingDay(iso, vacations) {
  if (!iso || isWorkingDay(iso, vacations)) return iso;
  let fwd = iso, back = iso;
  // Bound the walk — a 30-day window is comfortably more than any
  // sane vacation span. If nothing matches, return iso unchanged.
  for (let i = 1; i <= 30; i++) {
    fwd = addDaysISO(fwd, 1);
    back = addDaysISO(back, -1);
    const fwdOk = isWorkingDay(fwd, vacations);
    const backOk = isWorkingDay(back, vacations);
    if (fwdOk && backOk) return fwd; // tie → forward
    if (fwdOk) return fwd;
    if (backOk) return back;
  }
  return iso;
}

// Count working days from start to end (inclusive of start, exclusive
// of end). Helper used to capture the working-day duration of a bar at
// drag-start so 'move' mode can preserve it across weekend crossings.
export function workingDaysBetween(startIso, endIso, vacations) {
  if (!startIso || !endIso) return 0;
  if (startIso === endIso) return 0;
  const reverse = startIso > endIso;
  let a = reverse ? endIso : startIso;
  const b = reverse ? startIso : endIso;
  let count = 0;
  while (a < b) {
    if (isWorkingDay(a, vacations)) count++;
    a = addDaysISO(a, 1);
  }
  return reverse ? -count : count;
}

// Anchors per dependency type: which endpoint of pred drives, which
// endpoint of succ is constrained.
//   FS:  succ.start >= pred.end   + lag
//   FF:  succ.end   >= pred.end   + lag
//   SS:  succ.start >= pred.start + lag
//   SF:  succ.end   >= pred.start + lag
function predAnchor(type, item) {
  return type === 'FS' || type === 'FF' ? item.end : item.start;
}
function succAnchorKey(type) {
  return type === 'FS' || type === 'SS' ? 'start' : 'end';
}
// +1-day implicit gap for cross-anchor constraints (FS, SF) so the
// visual end-day inclusivity doesn't produce a 1-day overlap.
function anchorGap(type) {
  return type === 'FS' || type === 'SF' ? 1 : 0;
}

// Solve a single propagate pass. `change` updates `sourceKey` first;
// then we walk both forward (along outgoing edges from any newly-moved
// item) and backward (along incoming edges, to push predecessors back
// per decision A) until everything is consistent.
//
// Inputs:
//   items: [{ key, start, end }]  (current state)
//   edges: result of buildDependencyEdges
//   change: { sourceKey, newStart, newEnd }
//
// Returns:
//   { updates: [{ key, start, end }], error: null }
//   { updates: [],                    error: { code: 'CYCLE_DETECTED', path: [...] } }
export function applyConstraintsLocally(items, edges, change, opts = {}) {
  const vacations = Array.isArray(opts.vacations) ? opts.vacations : [];
  const working = new Map();
  for (const it of items || []) {
    working.set(it.key, { key: it.key, start: it.start || null, end: it.end || null });
  }
  const src = working.get(change.sourceKey);
  if (!src) return { updates: [], error: null };
  src.start = change.newStart || null;
  src.end = change.newEnd || null;

  // Group edges by endpoint for fast lookup.
  const incident = new Map(); // key → [{edge, role: 'pred'|'succ'}]
  for (const e of edges) {
    if (!incident.has(e.predKey)) incident.set(e.predKey, []);
    if (!incident.has(e.succKey)) incident.set(e.succKey, []);
    incident.get(e.predKey).push({ edge: e, role: 'pred' });
    incident.get(e.succKey).push({ edge: e, role: 'succ' });
  }

  const edited = new Map(); // key → 'trigger' | 'forward' | 'backward'
  edited.set(change.sourceKey, 'trigger');
  const queue = [change.sourceKey];
  const visited = new Set(); // edge:direction
  const maxIter = (items?.length || 0) * 4 + 10;
  let iter = 0;

  while (queue.length && iter < maxIter) {
    iter++;
    const k = queue.shift();
    const incidents = incident.get(k) || [];
    for (const { edge, role } of incidents) {
      const direction = role === 'pred' ? 'forward' : 'backward';
      const marker = `${edge.linkId}:${direction}`;
      if (visited.has(marker)) continue;
      visited.add(marker);

      const pred = working.get(edge.predKey);
      const succ = working.get(edge.succKey);
      if (!pred || !succ) continue;

      const predAnchorVal = predAnchor(edge.type, pred);
      const succKey = succAnchorKey(edge.type);
      const succAnchorVal = succ[succKey];

      const lagWithGap = (edge.lagDays || 0) + anchorGap(edge.type);
      // If either anchor is missing, fill from the constraint (decision C).
      if (!predAnchorVal && !succAnchorVal) continue; // can't anchor at all
      if (!predAnchorVal) {
        // pred missing → leave pred alone unless we're propagating backward
        // from a succ change. In that case set pred's anchor from succ's.
        if (direction === 'backward') {
          const newPredAnchor = addWorkingDays(succAnchorVal, -lagWithGap, vacations);
          shiftItemToAnchor(pred, edge.type === 'FS' || edge.type === 'FF' ? 'end' : 'start', newPredAnchor);
          edited.set(pred.key, 'cascade-backward');
          queue.push(pred.key);
        }
        continue;
      }
      if (!succAnchorVal) {
        // succ missing → auto-fill from pred
        const newSuccAnchor = addWorkingDays(predAnchorVal, lagWithGap, vacations);
        ensureBothDates(succ, succKey, newSuccAnchor);
        edited.set(succ.key, 'cascade-forward');
        queue.push(succ.key);
        continue;
      }

      const required = addWorkingDays(predAnchorVal, lagWithGap, vacations);
      const delta = dateDiffDays(succAnchorVal, required);
      if (delta == null || delta === 0) continue;
      // Same direction-based dispatch as the backend solver.
      if (direction === 'forward') {
        if (edited.get(succ.key) === 'trigger') {
          return cycleError(edge, change.sourceKey);
        }
        shiftItemBy(succ, delta);
        edited.set(succ.key, 'cascade-forward');
        queue.push(succ.key);
      } else {
        if (edited.get(pred.key) === 'trigger') {
          return cycleError(edge, change.sourceKey);
        }
        shiftItemBy(pred, -delta);
        edited.set(pred.key, 'cascade-backward');
        queue.push(pred.key);
      }
    }
  }

  if (iter >= maxIter) {
    return { updates: [], error: { code: 'CYCLE_DETECTED', path: [] } };
  }

  // Build updates list — only items whose dates actually changed.
  const updates = [];
  const orig = new Map((items || []).map(i => [i.key, i]));
  for (const [key, item] of working.entries()) {
    const o = orig.get(key);
    if (!o) continue;
    if ((item.start || null) !== (o.start || null) ||
        (item.end || null) !== (o.end || null)) {
      updates.push({ key, start: item.start, end: item.end });
    }
  }
  return { updates, error: null };
}

function shiftItemBy(item, days) {
  if (item.start) item.start = addDaysISO(item.start, days);
  if (item.end) item.end = addDaysISO(item.end, days);
}

// Move only one anchor; preserve duration if the other is set.
function shiftItemToAnchor(item, anchor, newVal) {
  if (anchor === 'start') {
    if (item.start && item.end) {
      const dur = dateDiffDays(item.start, item.end);
      item.start = newVal;
      item.end = addDaysISO(newVal, dur || 0);
    } else {
      item.start = newVal;
    }
  } else {
    if (item.start && item.end) {
      const dur = dateDiffDays(item.start, item.end);
      item.end = newVal;
      item.start = addDaysISO(newVal, -(dur || 0));
    } else {
      item.end = newVal;
    }
  }
}

// Auto-fill both dates of an item from one anchor when both were null.
// Default duration: 1 day (decision: confirmed).
function ensureBothDates(item, anchor, newVal) {
  if (anchor === 'start') {
    item.start = newVal;
    if (!item.end) item.end = addDaysISO(newVal, 1);
  } else {
    item.end = newVal;
    if (!item.start) item.start = addDaysISO(newVal, -1);
  }
}

function cycleError(edge, sourceKey) {
  return {
    updates: [],
    error: {
      code: 'CYCLE_DETECTED',
      path: [sourceKey, edge.predKey, edge.succKey],
    },
  };
}
